import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, Minus, Plus, ShoppingBag, X } from 'lucide-react';
import { formatMoney } from '@/lib/money';
import { optionsComplete, type Product, type SelectedOptions } from '@/lib/products';
import { SPRING_DEFAULT } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { isLightColor, type ThemePreset } from '@/lib/themes';
import { useCart } from './cart';

interface ProductSheetProps {
  product: Product | null;
  theme: ThemePreset;
  /** Visitors can add to the order. False when the shop / payments aren't live. */
  canOrder: boolean;
  preview?: boolean;
  onClose: () => void;
}

/**
 * Tap-a-product detail sheet: big photo, badges, price, full description,
 * bullet points, option pickers (dropdown or button group), quantity, and
 * "Add to order". Every option with values must be chosen before adding.
 */
export function ProductSheet({ product, theme, canOrder, preview, onClose }: ProductSheetProps) {
  const cart = useCart();
  const [selected, setSelected] = useState<SelectedOptions>({});
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const isLight = isLightColor(theme.cardBg);
  const border = isLight ? 'rgba(10,10,10,0.1)' : 'rgba(255,255,255,0.12)';

  // Reset per product.
  useEffect(() => {
    setSelected({});
    setQty(1);
    setAdded(false);
  }, [product?.id]);

  useEffect(() => {
    if (!product) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [product, onClose]);

  const complete = useMemo(() => (product ? optionsComplete(product, selected) : false), [product, selected]);

  function add() {
    if (!product || !cart || preview || !complete) return;
    cart.add(product, selected, qty);
    setAdded(true);
    setTimeout(() => onClose(), 650);
  }

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.button
            aria-label="Close"
            onClick={onClose}
            className="absolute inset-0 bg-ink/60 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={SPRING_DEFAULT}
            className="relative flex max-h-[92dvh] w-full max-w-md flex-col overflow-hidden rounded-t-2xl safe-pb"
            style={{ backgroundColor: theme.cardBg, color: theme.text }}
            role="dialog"
            aria-label={product.name}
          >
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-ink/45 text-bone backdrop-blur-md"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {/* Photo */}
              <div
                className="relative aspect-[4/3] w-full overflow-hidden"
                style={{ backgroundColor: theme.accent + '14' }}
              >
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className={cn('h-full w-full object-cover', !product.available && 'opacity-60 grayscale')}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center" style={{ color: theme.accent }}>
                    <ShoppingBag className="h-12 w-12 opacity-70" />
                  </div>
                )}
                {(product.badges.length > 0 || !product.available) && (
                  <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
                    {!product.available && <Badge bg={theme.text} fg={theme.cardBg}>Sold out</Badge>}
                    {product.badges.map((b) => (
                      <Badge key={b} bg={theme.accent} fg={theme.cardBg}>
                        {b}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-5 px-5 pb-5 pt-4">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="text-xl font-medium leading-tight tracking-tight">{product.name}</h2>
                  <p className="shrink-0 text-xl font-medium tabular-nums">
                    {formatMoney(product.priceCents, product.currency)}
                  </p>
                </div>

                {product.description && (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed" style={{ opacity: 0.85 }}>
                    {product.description}
                  </p>
                )}

                {product.bullets.length > 0 && (
                  <ul className="space-y-1.5">
                    {product.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm leading-snug" style={{ opacity: 0.9 }}>
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: theme.accent }} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {product.options.map((opt) =>
                  opt.values.length === 0 ? null : (
                    <div key={opt.id}>
                      <p className="mb-1.5 font-mono text-[10px] uppercase tracking-widest" style={{ opacity: 0.7 }}>
                        {opt.name}
                        {!selected[opt.name] && <span style={{ color: theme.accent }}> · choose one</span>}
                      </p>
                      {opt.type === 'buttons' ? (
                        <div className="flex flex-wrap gap-1.5">
                          {opt.values.map((v) => {
                            const on = selected[opt.name] === v;
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setSelected((s) => ({ ...s, [opt.name]: v }))}
                                aria-pressed={on}
                                className="h-9 rounded-full border px-3.5 text-sm font-medium tracking-tight transition-colors"
                                style={
                                  on
                                    ? { backgroundColor: theme.text, color: theme.cardBg, borderColor: theme.text }
                                    : { borderColor: border, color: theme.text }
                                }
                              >
                                {v}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="relative">
                          <select
                            value={selected[opt.name] ?? ''}
                            onChange={(e) => setSelected((s) => ({ ...s, [opt.name]: e.target.value }))}
                            className="h-11 w-full appearance-none rounded-md border bg-transparent px-3.5 pr-10 text-sm focus:outline-none"
                            style={{ borderColor: border, color: theme.text, backgroundColor: theme.cardBg }}
                            aria-label={opt.name}
                          >
                            <option value="" disabled>
                              Choose {opt.name.toLowerCase()}…
                            </option>
                            {opt.values.map((v) => (
                              <option key={v} value={v}>
                                {v}
                              </option>
                            ))}
                          </select>
                          <ChevronDown
                            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2"
                            style={{ opacity: 0.6 }}
                          />
                        </div>
                      )}
                    </div>
                  ),
                )}
              </div>
            </div>

            {/* Footer: qty + add */}
            <div className="flex items-center gap-3 border-t px-5 py-3" style={{ borderColor: border }}>
              {canOrder && product.available ? (
                <>
                  <div className="flex h-11 items-center rounded-full border" style={{ borderColor: border }}>
                    <button
                      type="button"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      aria-label="Decrease quantity"
                      className="flex h-11 w-10 items-center justify-center"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-6 text-center text-sm font-medium tabular-nums">{qty}</span>
                    <button
                      type="button"
                      onClick={() => setQty((q) => Math.min(99, q + 1))}
                      aria-label="Increase quantity"
                      className="flex h-11 w-10 items-center justify-center"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={add}
                    disabled={!complete || preview}
                    className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full text-sm font-medium tracking-tight transition-transform active:scale-[0.98] disabled:opacity-50"
                    style={{ backgroundColor: added ? theme.accent : theme.text, color: theme.cardBg }}
                  >
                    {added ? (
                      <>
                        <Check className="h-4 w-4" />
                        Added
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="h-4 w-4" />
                        {complete
                          ? `Add · ${formatMoney(product.priceCents * qty, product.currency)}`
                          : 'Choose options'}
                      </>
                    )}
                  </button>
                </>
              ) : (
                <p className="w-full text-center text-xs" style={{ opacity: 0.7 }}>
                  {!product.available ? 'Sold out for now.' : 'Ordering opens soon.'}
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Badge({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) {
  return (
    <span
      className="rounded-full px-2 py-0.5 font-mono text-[9px] font-medium uppercase tracking-widest"
      style={{ backgroundColor: bg, color: fg }}
    >
      {children}
    </span>
  );
}
